import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Table, message, Space } from "antd";
import { ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { Header } from "../components/layout/header";
import { listFeedback } from "../services/api";
import { exportToCsv } from "../utils/exportToCsv";
import { formatClinicTimestamp } from "../utils/formatTimestamp";

/**
 * Admin Feedback view: lists thumbs up/down feedback left on AI notes.
 *
 * Mirrors the TemplateLibrary admin-table page shape (Header + Card with a Back
 * button in the title and an antd Table). The backend feedback row includes a
 * resolved clinician_name, surfaced in a Clinician column and the CSV export.
 */

const CSV_COLUMNS = [
  { key: "created_at_display", header: "Date" },
  { key: "session_id", header: "Session" },
  { key: "clinician_name", header: "Clinician" },
  { key: "note_id", header: "Note" },
  { key: "rating", header: "Rating" },
  { key: "comments", header: "Comment" },
];

export default function FeedbackView({ options, selectedDoctorId, setSelectedDoctorId }) {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const logout = () => {
    instance.logout();
    sessionStorage.clear();
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await listFeedback();
        if (!cancelled) setRows(data?.feedback ?? []);
      } catch (e) {
        if (!cancelled) {
          message.error(
            e?.response?.data?.detail || e?.message || "Failed to load feedback."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: formatClinicTimestamp,
    },
    { title: "Session", dataIndex: "session_id", key: "session_id" },
    {
      title: "Clinician",
      dataIndex: "clinician_name",
      key: "clinician_name",
      render: (name) => name || "Unknown clinician",
    },
    { title: "Note", dataIndex: "note_id", key: "note_id" },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      render: (rating) => (rating === 1 ? "👍 Positive" : "👎 Negative"),
    },
    { title: "Comment", dataIndex: "comments", key: "comments" },
  ];

  const cardTitle = (
    <Space>
      <Button
        type="text"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/")}
        aria-label="Back to previous screen"
      >
        Back
      </Button>
      <span>Feedback</span>
    </Space>
  );

  return (
    <>
      <Header
        options={options}
        selectedDoctorId={selectedDoctorId}
        onSelectDoctor={setSelectedDoctorId}
        onLogout={logout}
      />
      <Card
        title={cardTitle}
        style={{ maxWidth: 1000, margin: "24px auto" }}
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              exportToCsv(
                "ai-note-feedback.csv",
                rows.map((r) => ({
                  ...r,
                  created_at_display: formatClinicTimestamp(r.created_at),
                })),
                CSV_COLUMNS
              )
            }
            disabled={!rows.length}
          >
            Export CSV
          </Button>
        }
      >
        <Table
          rowKey="id"
          dataSource={rows}
          loading={loading}
          columns={columns}
          pagination={false}
          locale={{ emptyText: "No feedback yet." }}
        />
      </Card>
    </>
  );
}
